import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "_shared/_stores/root-reducer";
import Guid from "_shared/guids/guid";
import type { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import { userNetworkConnectionsApi } from "users/networking/connections/_api/user-network-connection-api";

export type NetworkConnectionRemoval = {
  userId?: string;
  connectionId?: string;
};

export type NetworkConnectionsStoreState = {
  allUserIds: string[];
  byUserId: {
    entries: Record<string, UserInformationJSON>;
  };
  byConnectionId: {
    entries: Record<string, UserInformationJSON>;
  };
};

const initialState: NetworkConnectionsStoreState = {
  allUserIds: [],
  byUserId: {
    entries: {},
  },
  byConnectionId: {
    entries: {},
  },
};

function upsertNetworkConnectionJSON(
  state: NetworkConnectionsStoreState,
  connection: UserInformationJSON,
  options: { addToFront?: boolean } = {},
): void {
  if (connection.id) {
    state.byUserId.entries[connection.id] = connection;
    if (!state.allUserIds.includes(connection.id)) {
      if (options.addToFront) {
        state.allUserIds.unshift(connection.id);
      } else {
        state.allUserIds.push(connection.id);
      }
    }
  }

  if (connection.connectionId) {
    state.byConnectionId.entries[connection.connectionId] = connection;
  }
}

function removeNetworkConnectionJSON(
  state: NetworkConnectionsStoreState,
  removal: NetworkConnectionRemoval,
): void {
  const connectionByConnectionId = removal.connectionId
    ? state.byConnectionId.entries[removal.connectionId]
    : undefined;
  const connectionByUserId = removal.userId ? state.byUserId.entries[removal.userId] : undefined;
  const userId = removal.userId ?? connectionByConnectionId?.id;
  const connectionId = removal.connectionId ?? connectionByUserId?.connectionId ?? undefined;

  if (userId) {
    delete state.byUserId.entries[userId];
    state.allUserIds = state.allUserIds.filter((id) => id !== userId);
  }

  if (connectionId) {
    delete state.byConnectionId.entries[connectionId];
  }
}

const hasSearchQuery = (query: { query?: string } | void): boolean => Boolean(query?.query?.trim());

export const networkConnectionsSlice = createSlice({
  name: "networkConnections",
  initialState,
  reducers: {
    upsertNetworkConnection: (state, action: PayloadAction<UserInformationJSON>) => {
      upsertNetworkConnectionJSON(state, action.payload, { addToFront: true });
    },
    removeNetworkConnection: (state, action: PayloadAction<NetworkConnectionRemoval>) => {
      removeNetworkConnectionJSON(state, action.payload);
    },
    clearNetworkConnections: (state) => {
      state.allUserIds = [];
      state.byUserId.entries = {};
      state.byConnectionId.entries = {};
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(userNetworkConnectionsApi.endpoints.getUserNetworkConnections.matchFulfilled, (state, action) => {
      const isFullListResponse = !hasSearchQuery(action.meta.arg.originalArgs);
      const responseUserIds = action.payload.map((connection) => connection.id);

      if (isFullListResponse) {
        const responseUserIdSet = new Set(responseUserIds);
        for (const userId of Object.keys(state.byUserId.entries)) {
          if (!responseUserIdSet.has(userId)) {
            removeNetworkConnectionJSON(state, { userId });
          }
        }
        state.allUserIds = responseUserIds;
      }

      for (const connection of action.payload) {
        upsertNetworkConnectionJSON(state, connection);
      }
    });

    builder.addMatcher(userNetworkConnectionsApi.endpoints.removeNetworkConnection.matchFulfilled, (state, action) => {
      removeNetworkConnectionJSON(state, {
        connectionId: action.meta.arg.originalArgs.connectionId.value,
      });
    });
  },
});

export const {
  clearNetworkConnections,
  removeNetworkConnection,
  upsertNetworkConnection,
} = networkConnectionsSlice.actions;

const selectNetworkedUserIdSet = createSelector(
  [(state: RootState) => state.networkConnections.byUserId.entries],
  (entries) => new Set(Object.keys(entries)),
);

const selectNetworkConnections = createSelector(
  [
    (state: RootState) => state.networkConnections.allUserIds,
    (state: RootState) => state.networkConnections.byUserId.entries,
  ],
  (allUserIds, entries) => {
    const connectionJSON = allUserIds.length
      ? allUserIds.map((userId) => entries[userId]).filter((connection): connection is UserInformationJSON => !!connection)
      : Object.values(entries);

    return UserInformation.fromJSONArray(connectionJSON);
  },
);

const selectIsNetworkedWith = createSelector(
  [selectNetworkedUserIdSet, (_: RootState, otherUserId?: Guid) => otherUserId?.value],
  (set, otherUserIdValue) => (otherUserIdValue ? set.has(otherUserIdValue) : false),
);

const selectNetworkConnectionByUserId = createSelector(
  [(state: RootState) => state.networkConnections.byUserId.entries, (_: RootState, userId?: Guid) => userId?.value],
  (entries, userIdValue) => {
    if (!userIdValue) return undefined;
    return UserInformation.fromJSON(entries[userIdValue]);
  },
);

const selectNetworkConnectionByConnectionId = createSelector(
  [
    (state: RootState) => state.networkConnections.byConnectionId.entries,
    (_: RootState, connectionId?: Guid) => connectionId?.value,
  ],
  (entries, connectionIdValue) => {
    if (!connectionIdValue) return undefined;
    return UserInformation.fromJSON(entries[connectionIdValue]);
  },
);

export const getNetworkedUserIdSet = () => (state: RootState) => selectNetworkedUserIdSet(state);

export const getNetworkConnections = () => (state: RootState) => selectNetworkConnections(state);

export const getIsNetworkedWith = (otherUserId?: Guid) => (state: RootState) =>
  selectIsNetworkedWith(state, otherUserId);

export const getNetworkConnectionByUserId = (userId?: Guid) => (state: RootState) =>
  selectNetworkConnectionByUserId(state, userId);

export const getNetworkConnectionByConnectionId = (connectionId?: Guid) => (state: RootState) =>
  selectNetworkConnectionByConnectionId(state, connectionId);
